import React, { useState, useEffect, useCallback } from 'react';

const PlayerManager = () => {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');

  // Form states
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [isRegular, setIsRegular] = useState(true);

  const getApiUrl = () => {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:3000';
    } else if (hostname === '10.0.0.28') {
      return `https://${hostname}/api`;
    } else {
      return `https://${hostname}/api`;
    }
  };

  const loadPlayers = useCallback(async () => {
    try {
      const response = await fetch(`${getApiUrl()}/players`);
      const data = await response.json();
      setPlayers(data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading players:', error);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPlayers();
  }, [loadPlayers]);

  const resetForm = () => {
    setFirstName('');
    setLastName('');
    setPhone('');
    setEmail('');
    setIsRegular(true);
    setEditingPlayer(null);
  };

  const startCreatePlayer = () => {
    resetForm();
    setShowCreateForm(true);
  };

  const startEditPlayer = (player) => {
    setEditingPlayer(player);
    setFirstName(player.first_name);
    setLastName(player.last_name);
    setPhone(player.phone || '');
    setEmail(player.email || '');
    setIsRegular(player.is_regular);
    setShowCreateForm(true);
  };

  const savePlayer = async () => {
    if (!firstName || !lastName) {
      alert('First and last name are required');
      return;
    }

    try {
      const playerData = {
        first_name: firstName,
        last_name: lastName,
        phone: phone || null,
        email: email || null,
        is_regular: isRegular
      };

      let response;
      if (editingPlayer) {
        // Update existing player
        response = await fetch(`${getApiUrl()}/players/${editingPlayer.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(playerData)
        });
      } else {
        // Create new player
        response = await fetch(`${getApiUrl()}/players`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(playerData)
        });
      }

      if (response.ok) {
        alert(editingPlayer ? 'Player updated successfully!' : 'Player created successfully!');
        setShowCreateForm(false);
        resetForm();
        loadPlayers();
      } else {
        const errorData = await response.json();
        alert(`Error: ${errorData.error}`);
      }
    } catch (error) {
      console.error('Error saving player:', error);
      alert('Error saving player');
    }
  };

  const deletePlayer = async (playerId) => {
    if (!window.confirm('Are you sure you want to delete this player? This cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`${getApiUrl()}/players/${playerId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        loadPlayers();
        alert('Player deleted successfully!');
      } else {
        const errorData = await response.json();
        alert(`Error: ${errorData.error}`);
      }
    } catch (error) {
      console.error('Error deleting player:', error);
    }
  };

  // Filter players based on search and filter type
  const filteredPlayers = players.filter(player => {
    const matchesSearch = searchTerm === '' || 
      player.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      player.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (player.phone && player.phone.includes(searchTerm)) ||
      (player.email && player.email.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesFilter = filterType === 'all' ||
      (filterType === 'regular' && player.is_regular) ||
      (filterType === 'external' && !player.is_regular);

    return matchesSearch && matchesFilter;
  });

  if (loading) {
    return <div className="container">Loading players...</div>;
  }

  return (
    <div className="container">
      <h1>Player Management</h1>
      
      {/* Search and Filter */}
      <div className="card">
        <h3>Player Database ({players.length} total)</h3>
        
        <div style={{ display: 'flex', gap: '15px', marginBottom: '20px' }}>
          <div className="form-group" style={{ flex: '1' }}>
            <input 
              type="text"
              placeholder="Search players..."
              className="form-control"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <div className="form-group">
            <select 
              className="form-control"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
            >
              <option value="all">All Players</option>
              <option value="regular">Regular Members</option>
              <option value="external">External Players</option>
            </select>
          </div>
          
          <button className="btn btn-success" onClick={startCreatePlayer}>
            Add New Player
          </button>
        </div>

        {/* Create/Edit Form */}
        {showCreateForm && (
          <div style={{ marginTop: '20px', padding: '20px', background: '#f8f9fa', borderRadius: '5px' }}>
            <h4>{editingPlayer ? 'Edit Player' : 'Add New Player'}</h4>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
              <div className="form-group">
                <label>First Name: *</label>
                <input 
                  type="text"
                  className="form-control"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </div>
              
              <div className="form-group">
                <label>Last Name: *</label>
                <input 
                  type="text"
                  className="form-control"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Phone:</label>
                <input 
                  type="text"
                  className="form-control"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="0412 345 678"
                />
              </div>

              <div className="form-group">
                <label>Email:</label>
                <input 
                  type="email"
                  className="form-control"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="player@example.com"
                />
              </div>
            </div>

            <div className="form-group">
              <label>
                <input 
                  type="checkbox"
                  checked={isRegular}
                  onChange={(e) => setIsRegular(e.target.checked)}
                />
                {' '}Regular Club Member
              </label>
            </div>

            <div>
              <button className="btn btn-success" onClick={savePlayer}>
                {editingPlayer ? 'Update Player' : 'Create Player'}
              </button>
              <button 
                className="btn" 
                onClick={() => {
                  setShowCreateForm(false);
                  resetForm();
                }}
                style={{ marginLeft: '10px' }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Players List */}
        <div style={{ marginTop: '20px' }}>
          <p>Showing {filteredPlayers.length} of {players.length} players</p>
          
          {filteredPlayers.map(player => (
            <div key={player.id} style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              padding: '15px',
              margin: '5px 0',
              border: '1px solid #ddd',
              borderRadius: '5px',
              background: 'white'
            }}>
              <div>
                <strong>{player.first_name} {player.last_name}</strong>
                <span style={{ 
                  marginLeft: '10px',
                  padding: '2px 8px',
                  borderRadius: '3px',
                  fontSize: '12px',
                  background: player.is_regular ? '#d4edda' : '#fff3cd',
                  color: player.is_regular ? '#155724' : '#856404'
                }}>
                  {player.is_regular ? 'Regular' : 'External'}
                </span>
                <br />
                <small style={{ color: '#666' }}>
                  {player.phone && `Phone: ${player.phone} | `}
                  {player.email && `Email: ${player.email} | `}
                  Member since: {new Date(player.created_at).toLocaleDateString()}
                </small>
              </div>
              <div>
                <button 
                  className="btn"
                  onClick={() => startEditPlayer(player)}
                  style={{ marginRight: '5px' }}
                >
                  Edit
                </button>
                <button 
                  className="btn btn-danger"
                  onClick={() => deletePlayer(player.id)}
                  style={{ fontSize: '12px' }}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PlayerManager;
